import { useRef, useState } from "react";
import { Download, Upload, FileText } from "lucide-react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import { Table, THead, TR, TH, TD } from "../ui/Table";
import { stringifyCSV, downloadCSV, readCSVFile } from "../../lib/csv";

// Generic CSV import/export for simple tabular entities (Clients, Offers, Team, Tools).
// For Payments (GHL format with client matching), use the dedicated PaymentsCSVImport.

export default function CSVActions({
  entity, // "clients", "offers", "team", "tools"
  columns, // array of column names (also used as CSV headers)
  records,
  sampleRow, // an example record object showing each column's expected value
  onImport, // (rows: object[]) => void
  beforeImport, // optional transform before passing to onImport
}) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const handleExport = () => {
    const exportRecords = records.map((r) => {
      const obj = {};
      columns.forEach((c) => { obj[c] = r[c] ?? ""; });
      return obj;
    });
    const csv = stringifyCSV(exportRecords, columns);
    downloadCSV(`${entity}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handleSample = () => {
    const csv = stringifyCSV([sampleRow], columns);
    downloadCSV(`${entity}-template.csv`, csv);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await readCSVFile(file);
      const transformed = beforeImport ? beforeImport(parsed.records) : parsed.records;
      const wasTransformed = beforeImport && transformed !== parsed.records && transformed.length !== parsed.records.length;
      setPreview({ rows: transformed, fileName: file.name, originalCount: parsed.records.length, wasTransformed });
    } catch (err) {
      alert("Could not parse CSV: " + err.message);
    }
    e.target.value = "";
  };

  const confirmImport = () => {
    if (preview?.rows?.length) onImport(preview.rows);
    setPreview(null);
  };

  return (
    <>
      <div className="inline-flex gap-1.5">
        <Button variant="secondary" size="sm" onClick={handleExport} title="Export CSV"><Download size={13} /> Export</Button>
        <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}><Upload size={13} /> Import</Button>
        <button onClick={handleSample} className="text-xs text-brand-slate hover:text-brand-blue inline-flex items-center gap-1 px-2 py-1.5" title="Download sample template">
          <FileText size={13} /> Sample
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
      </div>

      <Modal open={!!preview} onClose={() => setPreview(null)} title={`Import ${entity} — ${preview?.rows?.length || 0} rows`} size="lg">
        {preview && (
          <div className="space-y-3">
            <p className="text-sm text-brand-slate">Review the rows from <span className="font-mono">{preview.fileName}</span> before importing. New records will be added — existing data is not touched.</p>
            {preview.wasTransformed && (
              <div className="bg-brand-blue-soft border border-blue-200 rounded-lg p-3 text-xs text-brand-blue-dark space-y-1.5">
                <div className="font-medium">Detected GoHighLevel invoice CSV.</div>
                <div>Extracted {preview.rows.length} unique customers from {preview.originalCount} invoice rows (deduplicated by email).</div>
                <div>For each customer, the most-common "Line Item Name" becomes their primary offer (auto-created if it doesn't exist yet).</div>
                <div className="pt-1 border-t border-blue-200/60"><strong>Next step:</strong> after this import finishes, go to the <strong>Payments</strong> tab and import the same CSV there to populate invoice history — payments will auto-match to these clients by email.</div>
              </div>
            )}
            <div className="max-h-[400px] overflow-auto border border-gray-100 dark:border-[var(--border-soft)] rounded-lg">
              <Table>
                <THead>
                  <TR>{columns.map((c) => <TH key={c}>{c}</TH>)}</TR>
                </THead>
                <tbody>
                  {preview.rows.slice(0, 100).map((row, i) => (
                    <TR key={i}>
                      {columns.map((c) => <TD key={c} className="text-xs">{row[c] || "—"}</TD>)}
                    </TR>
                  ))}
                </tbody>
              </Table>
            </div>
            {preview.rows.length > 100 && (
              <p className="text-xs text-brand-slate">Showing first 100 of {preview.rows.length} rows. All will be imported.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPreview(null)}>Cancel</Button>
              <Button onClick={confirmImport}>Import {preview.rows.length} rows</Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
